import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";

/**
 * A speaker: an initials avatar and the name, semibold. Reads the row
 * (`name`, `isUnnamed`); set `valuePath` to any always-present field
 * (`id`) so TableCell's populated check doesn't fall back to "-".
 *
 * The unnamed row — annotations with no speaker name (app reviews,
 * survey rows) gathered as one — reads grey with a "?" avatar, so it is
 * never mistaken for a person called Unnamed.
 */
const Avatar = styled.span`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;
const Name = styled(UI_BODY_SEMIBOLD_SM_DF)`
  color: ${colors2.grey80};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UNNAMED_LABEL = "Unnamed speakers";

// "Priya Nair" → "PN", "mwĩhaki" → "M", "Reviewer, 4★" → "R4".
export const initialsOf = (name) => {
  const parts = String(name || "")
    .split(/[\s,]+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

const TableSpeaker = ({ tableRowData }) => {
  const unnamed = !!tableRowData?.isUnnamed || !tableRowData?.name;
  const name = unnamed ? UNNAMED_LABEL : tableRowData.name;
  const nameEl = (
    <Name style={unnamed ? { color: colors2.grey60, fontWeight: 400 } : undefined}>
      {name}
    </Name>
  );
  // Named speakers link to their detail page; the unnamed aggregate row has no
  // single speaker to open, so it stays plain.
  const canLink = !unnamed && !!tableRowData?.id;
  return (
    <Row title={name}>
      <Avatar>
        <UI_CAPTION_SEMIBOLD_DF style={{ color: "inherit" }}>{unnamed ? "?" : initialsOf(name)}</UI_CAPTION_SEMIBOLD_DF>
      </Avatar>
      {canLink ? (
        <Link
          to={`/customers/${encodeURIComponent(tableRowData.id)}`}
          style={{ minWidth: 0, overflow: "hidden", textDecoration: "none", color: "inherit" }}
        >
          {nameEl}
        </Link>
      ) : (
        nameEl
      )}
    </Row>
  );
};

export default TableSpeaker;
